/**
 * Pure helpers for the draft-PO page: labeling, money formatting, and quantity parsing. The old
 * client-side CSV/clipboard export builders were removed 2026-08-08 (owner: never used — Create PO
 * superseded them; see docs/inventory-ui-refresh-plan-v1.md).
 */
import type { components } from '@/lib/api/types.gen';

export type PurchaseDraft = components['schemas']['PurchaseDraft'];
export type PurchaseDraftLine = components['schemas']['PurchaseDraftLine'];

export function draftLabel(
  l: Pick<PurchaseDraftLine, 'brandName' | 'styleName' | 'ssSku'>,
): string {
  return [l.brandName, l.styleName].filter(Boolean).join(' ') || l.ssSku;
}

export function draftVariant(l: Pick<PurchaseDraftLine, 'colorName' | 'sizeName'>): string {
  return [l.colorName, l.sizeName].filter(Boolean).join(' · ');
}

export function money(n: number | null): string {
  return n === null ? '—' : `$${n.toFixed(2)}`;
}

/**
 * Parse an operator-typed order quantity into the number actually used for the total + export, so the
 * displayed value can never diverge from what's ordered: blank → 0 (a cleared line orders nothing),
 * a plain non-negative integer → that value, anything else (negative, decimal, `1e3`, garbage) → null
 * (invalid — the page flags it red and blocks export until fixed).
 */
export function parseOrderQty(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return 0;
  return /^\d+$/.test(t) ? Number(t) : null;
}

/** One orderable row — already reduced to the operator-adjusted quantity + recomputed line total. */
export type DraftOrderRow = {
  ssSku: string;
  label: string;
  variant: string;
  onHand: number;
  orderQty: number;
  unitCost: number | null;
  lineTotal: number | null;
};
